import { useMemo } from 'react'
import type { WeeklyPlanVsFactRow } from '@/hooks/useWeeklyPlanVsFact'
import type { PlanData } from '@/hooks/useStudioAnalytics'
import { hasAnyPlanValues } from '@/lib/weeklyPlan'

interface StudioLite {
  studio_internal_id: number
  studio_number: string
}

export interface MissingPlanStudio {
  studio_internal_id: number
  studio_number: string
}

interface UseGlobalWeeklyPlanDiagnosticsParams {
  studioIds: number[]
  analyticsStudios: StudioLite[]
  periodId: number | null
  planData: PlanData | null
  rawDataReady?: boolean
  /** Сырые per-studio weeklyPlans из батчевого запроса useMultiStudioAnalytics —
   *  используются только для пер-студийных диагностик (missingPlan / zeroPlanRevenue),
   *  не для агрегации. */
  rawWeeklyPlans?: WeeklyPlanVsFactRow[]
  /** Сырые per-studio periodTotals — для zeroRevenueStudios. */
  rawPeriodTotals?: Array<{ studio_internal_id: number; total_gross_revenue: number | null }>
  /** Готовый агрегированный недельный план-факт из RPC get_aggregated_weekly_plan_vs_fact —
   *  серверная COALESCE+SUM, корректно учитывающая plan-no-fact и fact-no-plan студии. */
  rawAggregatedWeeklyPlans?: WeeklyPlanVsFactRow[]
}

export function useGlobalWeeklyPlanDiagnostics({
  studioIds,
  analyticsStudios,
  periodId,
  planData,
  rawDataReady,
  rawWeeklyPlans,
  rawPeriodTotals,
  rawAggregatedWeeklyPlans
}: UseGlobalWeeklyPlanDiagnosticsParams) {
  const result = useMemo(() => {
    const empty = {
      weeklyPlans: [] as WeeklyPlanVsFactRow[],
      missingPlanStudios: [] as string[],
      missingPlanStudioDetails: [] as MissingPlanStudio[],
      zeroRevenueStudios: [] as string[],
      zeroPlanRevenueStudios: [] as string[],
      hasAnyPlanInGlobal: false
    }

    if (!periodId || studioIds.length === 0 || !rawDataReady || !rawWeeklyPlans || !rawPeriodTotals) {
      return empty
    }

    // Пер-студийные диагностики: какие студии без плана, у каких студий план без выручки, у каких факт = 0.
    const rowsByStudio = new Map<number, WeeklyPlanVsFactRow[]>()
    for (const row of rawWeeklyPlans) {
      const existing = rowsByStudio.get(row.studio_internal_id) || []
      existing.push(row)
      rowsByStudio.set(row.studio_internal_id, existing)
    }

    const studioHasPlan = new Map<number, boolean>()
    const studioPlanRevenue = new Map<number, number>()
    const missingStudioNumbers: string[] = []
    const missingStudioDetails: MissingPlanStudio[] = []

    for (const studio of analyticsStudios) {
      const studioRows = rowsByStudio.get(studio.studio_internal_id) || []
      const hasPlan = studioRows.some(hasAnyPlanValues)
      studioHasPlan.set(studio.studio_internal_id, hasPlan)
      if (hasPlan) {
        const planRevenue = studioRows.reduce((sum, week) => sum + (week.weekly_gross_revenue_plan || 0), 0)
        studioPlanRevenue.set(studio.studio_internal_id, planRevenue)
      } else {
        missingStudioNumbers.push(studio.studio_number)
        missingStudioDetails.push({
          studio_internal_id: studio.studio_internal_id,
          studio_number: studio.studio_number
        })
      }
    }

    const anyStudioHasPlan = Array.from(studioHasPlan.values()).some(Boolean)
    const zeroPlanRevenueStudioNumbers = analyticsStudios
      .filter((studio) => studioHasPlan.get(studio.studio_internal_id) && (studioPlanRevenue.get(studio.studio_internal_id) || 0) === 0)
      .map((studio) => studio.studio_number)

    const revenueByStudio = new Map<number, number>()
    for (const row of rawPeriodTotals) {
      revenueByStudio.set(row.studio_internal_id, row.total_gross_revenue || 0)
    }

    const zeroRevenueStudioNumbers = analyticsStudios
      .filter((studio) => (revenueByStudio.get(studio.studio_internal_id) || 0) === 0)
      .map((studio) => studio.studio_number)

    // Агрегированные недельные строки приходят из SQL уже посчитанные. RPC сам делает
    // COALESCE(col, 0) + SUM, поэтому plan-no-fact студия даёт 0 на стороне факта,
    // fact-no-plan — 0 на стороне плана, а отсутствие строки в FULL JOIN view не
    // создаёт пустую запись в агрегате.
    const aggregatedRows = (rawAggregatedWeeklyPlans ?? []).slice().sort(
      (a, b) => a.week_number_in_period - b.week_number_in_period
    )

    return {
      weeklyPlans: aggregatedRows,
      missingPlanStudios: missingStudioNumbers,
      missingPlanStudioDetails: missingStudioDetails,
      zeroRevenueStudios: zeroRevenueStudioNumbers,
      zeroPlanRevenueStudios: zeroPlanRevenueStudioNumbers,
      hasAnyPlanInGlobal: anyStudioHasPlan
    }
  }, [studioIds, analyticsStudios, periodId, rawDataReady, rawWeeklyPlans, rawPeriodTotals, rawAggregatedWeeklyPlans])

  const shouldHideDelta = useMemo(() => {
    const isGlobalPlanRevenueZero = result.hasAnyPlanInGlobal && (planData?.total_gross_revenue_plan || 0) === 0
    return isGlobalPlanRevenueZero || !result.hasAnyPlanInGlobal
  }, [result.hasAnyPlanInGlobal, planData?.total_gross_revenue_plan])

  return {
    weeklyPlans: result.weeklyPlans,
    loading: !rawDataReady,
    missingPlanStudios: result.missingPlanStudios,
    missingPlanStudioDetails: result.missingPlanStudioDetails,
    zeroRevenueStudios: result.zeroRevenueStudios,
    zeroPlanRevenueStudios: result.zeroPlanRevenueStudios,
    hasAnyPlanInGlobal: result.hasAnyPlanInGlobal,
    shouldHideDelta
  }
}
